import React from "react";

interface ButtonProps {
  children?: React.ReactNode;
  onClick?: () => void;
  variant?: string;
  type?: "button" | "submit" | "reset";
  size?: "xs" | "sm" | "md" | "lg";
  isLoading?: boolean;
  disabled?: boolean;
  icon?: React.ReactNode;
  className?: string;
}

const Button = ({
  children,
  onClick,
  variant = "primary",
  type = "button",
  size = "md",
  isLoading = false,
  disabled = false,
  icon = null,
  className = "",
}: ButtonProps) => {
  // Shared base styles
  const baseStyles = [
    "rounded-lg", // Slightly more rounded corners for a modern feel
    "font-semibold",
    "focus:outline-none",
    "focus:ring-2",
    "focus:ring-offset-2",
    "transition-all",
    "ease-in-out",
    "duration-200",
    "inline-flex",
    "items-center",
    "justify-center",
    "tracking-wide",
    "shadow-sm", // Soft shadow
  ].join(" ");

  // Variant-based styles (can be tweaked to your taste)
  const variantStyles: Record<string, string> = {
    primary: [
      "bg-gradient-to-r",
      "from-primary-light",
      "to-primary",
      "text-text-on-primary",
      "hover:from-primary",
      "hover:to-primary-dark",
      "focus:ring-primary",
    ].join(" "),
    secondary: [
      "bg-gradient-to-r",
      "from-secondary-light",
      "to-secondary",
      "text-text-on-primary",
      "hover:from-secondary",
      "hover:to-secondary-dark",
      "focus:ring-secondary",
    ].join(" "),
    success: [
      "bg-gradient-to-r",
      "from-success-light",
      "to-success",
      "text-text-on-primary",
      "hover:from-success",
      "hover:to-success-dark",
      "focus:ring-success",
    ].join(" "),
    danger: [
      "bg-gradient-to-r",
      "from-danger-light",
      "to-danger",
      "text-text-on-primary",
      "hover:from-danger",
      "hover:to-danger-dark",
      "focus:ring-danger",
    ].join(" "),
    warning: [
      "bg-gradient-to-r",
      "from-warning-light",
      "to-warning",
      "text-text-on-primary",
      "hover:from-warning",
      "hover:to-warning-dark",
      "focus:ring-warning",
    ].join(" "),

    // Outline variants
    "primary-outline": [
      "border",
      "border-primary",
      "text-primary",
      "bg-transparent",
      "hover:bg-primary hover:text-text-on-primary",
      "focus:ring-primary",
    ].join(" "),
    "secondary-outline": [
      "border",
      "border-secondary",
      "text-secondary",
      "bg-transparent",
      "hover:bg-secondary hover:text-text-on-primary",
      "focus:ring-secondary",
    ].join(" "),
    "success-outline": [
      "border",
      "border-success",
      "text-success",
      "bg-transparent",
      "hover:bg-success hover:text-text-on-primary",
      "focus:ring-success",
    ].join(" "),
    "danger-outline": [
      "border",
      "border-danger",
      "text-danger",
      "bg-transparent",
      "hover:bg-danger hover:text-text-on-primary",
      "focus:ring-danger",
    ].join(" "),
    "warning-outline": [
      "border",
      "border-warning",
      "text-warning",
      "bg-transparent",
      "hover:bg-warning hover:text-text-on-primary",
      "focus:ring-warning",
    ].join(" "),

    // Text variants
    "primary-text": [
      "text-primary",
      "bg-transparent",
      "hover:underline",
      "focus:ring-primary",
    ].join(" "),
    "secondary-text": [
      "text-secondary",
      "bg-transparent",
      "hover:underline",
      "focus:ring-secondary",
    ].join(" "),
    "success-text": [
      "text-success",
      "bg-transparent",
      "hover:underline",
      "focus:ring-success",
    ].join(" "),
    "danger-text": [
      "text-danger",
      "bg-transparent",
      "hover:underline",
      "focus:ring-danger",
    ].join(" "),
    "warning-text": [
      "text-warning",
      "bg-transparent",
      "hover:underline",
      "focus:ring-warning",
    ].join(" "),
  };

  // Size-based styles
  const sizeStyles: Record<string, string> = {
    xs: "px-2 py-1 text-xs",
    sm: "px-3 py-1.5 text-sm",
    md: "px-4 py-2 text-base",
    lg: "px-6 py-3 text-lg",
  };

  // Spinner color for each variant
  const spinnerColor: Record<string, string> = {
    primary: "text-white",
    secondary: "text-white",
    success: "text-white",
    danger: "text-white",
    warning: "text-white",
    "primary-outline": "text-primary",
    "secondary-outline": "text-secondary",
    "success-outline": "text-success",
    "danger-outline": "text-danger",
    "warning-outline": "text-warning",
    "primary-text": "text-primary",
    "secondary-text": "text-secondary",
    "success-text": "text-success",
    "danger-text": "text-danger",
    "warning-text": "text-warning",
  };

  return (
    <button
      type={type}
      onClick={onClick}
      className={[
        baseStyles,
        variantStyles[variant] || variantStyles["primary"],
        sizeStyles[size],
        isLoading || disabled ? "cursor-not-allowed opacity-60" : "",
        className,
      ].join(" ")}
      disabled={isLoading || disabled}
    >
      {isLoading && (
        <svg
          className={`animate-spin h-5 w-5 ${spinnerColor[variant] || "text-white"} mr-2`}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 
              5.373 0 12h4zm2 5.291A7.962 7.962 0 014
              12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      )}
      {/* Optional icon (display if not loading) */}
      {!isLoading && icon && (
        <span className={children ? "mr-2" : ""}>{icon}</span>
      )}
      {children}
    </button>
  );
};

export default Button;
